import React, { ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { SizeType } from "antd/es/config-provider/SizeContext";

import Currency from "@mapmycustomers/shared/types/Currency";
import { Labeled, SelectField } from "@mapmycustomers/ui";

import { getCurrencies } from "@app/store/referenceData";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

interface Props {
  className?: string;
  currencies: Currency[];
  disabled?: boolean;
  label?: string;
  onChange?: (currency?: Currency["code"] | Currency["id"]) => void;
  size?: SizeType;
  useCurrencyId?: boolean;
  value?: Currency["code"] | Currency["id"];
}

const CurrencySelect: React.FC<Props> = ({
  className,
  currencies,
  disabled,
  label,
  onChange,
  size,
  useCurrencyId,
  value,
}) => {
  const analyticIssuer = useAnalytics();

  const selectedCurrency = useMemo(
    () =>
      currencies.find((currency) =>
        useCurrencyId ? currency.id === value : currency.code === value
      ),
    [currencies, value, useCurrencyId]
  );

  const handleChange = useCallback(
    (currencyId: Currency["id"]) => {
      const selectedCurrencyObject = currencies.find(({ id }) => id === currencyId);
      onChange?.(useCurrencyId ? selectedCurrencyObject?.id : selectedCurrencyObject?.code);
      analyticIssuer?.clicked(["Org Currency"], { currency: selectedCurrencyObject?.code });
    },
    [analyticIssuer, currencies, onChange, useCurrencyId]
  );

  const handleFilterOption = useCallback((inputValue: string, option?: { label?: ReactNode }) => {
    return (
      (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase().trim()) ?? false
    );
  }, []);

  const currencyOptions = useMemo(
    () => currencies.map(({ id, code, name }) => ({ label: `(${code}) ${name}`, value: id })),
    [currencies]
  );

  return (
    <Labeled label={label}>
      <SelectField
        className={className}
        disabled={disabled}
        filterOption={handleFilterOption}
        onChange={handleChange}
        options={currencyOptions}
        showSearch
        size={size}
        value={selectedCurrency?.id}
      />
    </Labeled>
  );
};

const mapStateToProps = (state: RootState) => ({
  currencies: getCurrencies(state),
});

export default connect(mapStateToProps)(CurrencySelect);
